import { DraggableWrapper } from '@/components/SearchAssistant/components/DraggableWrapper';
import { useAnimations } from '@/hooks/useAnimation';
import { searchAssistantUIStore } from '@/stores/searchAssistantStore';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';

const styles = {
  chatWindowWrapper: {
    position: 'relative',
    zIndex: 10,
    backgroundColor: (theme) => theme.palette.baseColors.offWhite.c100,
    boxShadow: (theme) => theme.shadows[2],
  },
  draggable: {
    position: 'absolute',
    top: 0,
    left: '5rem',
    zIndex: 20,
    width: 'calc(100% - 8rem)',
    height: '3rem',
  },
};

const ANIMATION_CONSTANTS = {
  DURATION: 300,
  EASING: 'cubic-bezier(0.5, 0, 0, 1)',
  SCALE_FACTOR: 0.9,
  TRANSLATE_OFFSET: '10%',
};

const animationOptions = {
  duration: ANIMATION_CONSTANTS.DURATION,
  easing: ANIMATION_CONSTANTS.EASING,
  fill: 'both',
};

const openKeyframes = [
  {
    opacity: 0,
    transform: `scale(${ANIMATION_CONSTANTS.SCALE_FACTOR}) translate3d(0, ${ANIMATION_CONSTANTS.TRANSLATE_OFFSET}, 0)`,
  },
  { opacity: 1, transform: 'scale(1) translate3d(0, 0, 0)' },
];

const closeKeyframes = [
  { opacity: 1, transform: 'scale(1) translate3d(0, 0, 0)' },
  {
    opacity: 0,
    transform: `scale(${ANIMATION_CONSTANTS.SCALE_FACTOR}) translate3d(0, ${ANIMATION_CONSTANTS.TRANSLATE_OFFSET}, 0)`,
  },
];

const ANIMATION_STATES = {
  FINISHED: 'finished',
};

export const ChatContainer = ({ children }) => {
  const { isOpen, size } = searchAssistantUIStore((state) => ({
    isOpen: state.isOpen,
    size: state.size,
  }));

  const [shouldRender, setShouldRender] = useState(isOpen);

  const { ref: animationRef, animate } = useAnimations({
    autoPlay: false,
    onFinish: ({ animation }) => {
      if (!isOpen && animation.playState === ANIMATION_STATES.FINISHED) {
        setShouldRender(false);
      }
    },
  });

  useEffect(() => {
    if (isOpen && !shouldRender) {
      setShouldRender(true);
      return;
    }

    if (animate) {
      animate({
        keyframes: isOpen ? openKeyframes : closeKeyframes,
        animationOptions,
      });
    }
  }, [isOpen, animate, shouldRender]);

  if (!shouldRender) {
    return null;
  }

  return (
    <DraggableWrapper
      elementWidth={size.width}
      elementHeight={size.height}
      dragHandle={<Box sx={styles.draggable} />}
    >
      <Box
        sx={styles.chatWindowWrapper}
        ref={animationRef}
        style={{
          width: size.width,
          height: size.height,
        }}
      >
        {children}
      </Box>
    </DraggableWrapper>
  );
};
